import { useInfiniteQuery } from '@tanstack/react-query';
import {
  getTradingOrderListOrdersQueryKey,
  getTradingPositionListPositionsQueryKey,
  useTradingOrderListOrdersHook,
  useTradingPositionListPositionsHook,
} from '@/lib/endpoints';
import { TradingOrderListOrdersStatus } from '@/lib/models';
import type { PlaeeOrderResponseDto, PlaeePositionResponseDto } from '@/lib/models';

/** History page size — small enough to render instantly, large enough to fill a phone screen twice. */
export const HISTORY_PAGE_SIZE = 20;

const FINISHED_STATUSES = 'closed,settled';

/**
 * A full page means there may be more; a short page is the end. `pages.length + 1`
 * works because pages are only ever appended in order (no jumping).
 */
function nextPage<T>(lastPage: T[], pages: T[][]): number | undefined {
  return lastPage.length === HISTORY_PAGE_SIZE ? pages.length + 1 : undefined;
}

/**
 * Infinite feed of finished (sold/settled) positions for the History tab —
 * pages `GET /plaee/positions?statuses=closed,settled` by `limit` + `page`.
 * The query key extends the plain list key, so the account-wide trading-cache
 * invalidation (prefix match on `/plaee/positions`) resets it too.
 */
export function useInfiniteFinishedPositions(enabled: boolean) {
  const fetchPositions = useTradingPositionListPositionsHook();
  return useInfiniteQuery({
    queryKey: [...getTradingPositionListPositionsQueryKey({ statuses: FINISHED_STATUSES }), 'infinite'],
    queryFn: ({ pageParam, signal }) =>
      fetchPositions({ statuses: FINISHED_STATUSES, limit: HISTORY_PAGE_SIZE, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: nextPage<PlaeePositionResponseDto>,
    enabled,
  });
}

/**
 * Infinite feed of cancelled orders for the History tab — pages
 * `GET /plaee/orders?status=cancelled` by `limit` + `page`. Runs in parallel
 * with the finished-positions feed; the page merges both into one list.
 */
export function useInfiniteCancelledOrders(enabled: boolean) {
  const fetchOrders = useTradingOrderListOrdersHook();
  return useInfiniteQuery({
    queryKey: [...getTradingOrderListOrdersQueryKey({ status: TradingOrderListOrdersStatus.cancelled }), 'infinite'],
    queryFn: ({ pageParam, signal }) =>
      fetchOrders({ status: TradingOrderListOrdersStatus.cancelled, limit: HISTORY_PAGE_SIZE, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: nextPage<PlaeeOrderResponseDto>,
    enabled,
  });
}
